import React from 'react'
import scrollToAnchorAppUtil from 'client/utils/dom/app.js'
import logEventAnalyticsUtil from 'modules/analytics/utils/log-event'

/**
 * Strips inline markdown formatting from a string.
 * Removes: `code`, **bold**, *italic*, [links](url), etc.
 */
function stripInlineMd (text) {
  return text
    .replace(/`([^`]+)`/g, '$1') // `code`
    .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
    .replace(/\*([^*]+)\*/g, '$1') // *italic*
    .replace(/_([^_]+)_/g, '$1') // _italic_
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [text](url)
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // ![alt](img)
    .trim()
}

/**
 * Converts a heading text into a URL-safe slug.
 * - Lowercase
 * - Trim whitespace
 * - Replace spaces with hyphens
 * - Remove non-alphanumeric except hyphens
 * - Normalize multiple dashes to single dash
 * - Remove leading/trailing hyphens
 */
function slugify (text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Ensures a slug is unique by appending -2, -3, etc. if needed.
 */
function ensureUniqueSlug (slug, slugMap) {
  if (!slugMap.has(slug)) {
    slugMap.set(slug, 1)
    return slug
  }

  let counter = 2
  let uniqueSlug = `${slug}-${counter}`
  while (slugMap.has(uniqueSlug)) {
    counter++
    uniqueSlug = `${slug}-${counter}`
  }
  slugMap.set(uniqueSlug, 1)
  return uniqueSlug
}

/**
 * Parses markdown headings from a string.
 * Ignores headings inside:
 * - Fenced code blocks (```)
 * - Blockquotes (>)
 * - Markdown tables (lines with |)
 *
 * Returns an array of { level, text, slug }
 */
function parseHeadings (markdown, minLevel = 2, maxLevel = 3) {
  const lines = markdown.split('\n')
  const headings = []
  const slugMap = new Map()
  let inCodeBlock = false
  let inTable = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Toggle code block state
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock
      continue
    }

    // Skip lines inside code blocks
    if (inCodeBlock) {
      continue
    }

    // Detect table lines (contains |)
    if (line.includes('|')) {
      inTable = true
      continue
    } else if (inTable && line.trim() === '') {
      inTable = false
      continue
    }

    // Skip if in table or blockquote
    if (inTable || line.trim().startsWith('>')) {
      continue
    }

    // Match ATX-style headings (# Heading)
    const match = line.match(/^(#{1,6})\s+(.+)$/)
    if (match) {
      const level = match[1].length
      if (level >= minLevel && level <= maxLevel) {
        const text = stripInlineMd(match[2])
        const slug = ensureUniqueSlug(slugify(text), slugMap)
        headings.push({ level, text, slug })
      }
    }
  }

  return headings
}

/**
 * ArticleTableOfContents Component
 *
 * Generates a nested table of contents from markdown headings.
 *
 * Props:
 * - value: string                      // markdown content
 * - minHeadingLevel?: number           // default 2
 * - maxHeadingLevel?: number           // default 5 (includes H2-H5 headings)
 * - titleText?: string                 // default "Table of Contents"
 * - className?: string                 // additional CSS classes
 * - initialVisibleItems?: number       // default 2 (top-level items when collapsed)
 * - initialVisibleNestedItems?: number // default 2 (nested items per parent when collapsed, applies to all levels)
 *
 * Nested structure: H2 → H3 → H4 → H5
 *
 * Testing notes:
 * - Ignores headings in code blocks, blockquotes, and tables
 * - Handles duplicate headings with -2, -3 suffixes
 * - Strips inline markdown from heading text
 * - Skips trailing "Conclusion" heading and headings containing "references"
 * - Limits nested children when collapsed to prevent overly long TOC
 */
export function ArticleTableOfContents ({
  value,
  minHeadingLevel = 2,
  maxHeadingLevel = 5,
  titleText = 'Table of Contents',
  className = '',
  initialVisibleItems = 2,
  initialVisibleNestedItems = 2
}) {
  const [isExpanded, setIsExpanded] = React.useState(false)

  const handleClickLink = React.useCallback((event, slug) => {
    event.preventDefault()

    logEventAnalyticsUtil('ArticleTableOfContents: Click TOC Link', {
      slug
    })

    // Update URL hash without scrolling
    window.history.pushState(null, '', `#${slug}`)
    // Use the app util which handles scroll + highlight
    scrollToAnchorAppUtil({
      location: { hash: `#${slug}` },
      hasDelay: false
    })
  }, [])

  const handleToggleExpand = React.useCallback(() => {
    const newExpandedState = !isExpanded
    setIsExpanded(newExpandedState)

    logEventAnalyticsUtil('ArticleTableOfContents: Toggle Expand', {
      isExpanded: newExpandedState
    })
  }, [isExpanded])

  const handleLinkMouseEnter = React.useCallback((event) => {
    event.target.style.color = '#004499'
    event.target.style.textDecoration = 'underline'
  }, [])

  const handleLinkMouseLeave = React.useCallback((event) => {
    event.target.style.color = '#0066cc'
    event.target.style.textDecoration = 'none'
  }, [])

  const headings = React.useMemo(() => {
    if (!value) {
      return []
    }

    let parsed = parseHeadings(value, minHeadingLevel, maxHeadingLevel)

    // Filter out headings containing "references"
    parsed = parsed.filter(heading => {
      const lowerText = heading.text.toLowerCase()
      return !lowerText.includes('references')
    })

    // Skip trailing "Conclusion" if it's the last heading
    if (parsed.length > 0) {
      const last = parsed[parsed.length - 1]
      if (last.text.toLowerCase() === 'conclusion') {
        parsed = parsed.slice(0, -1)
      }
    }

    return parsed
  }, [value, minHeadingLevel, maxHeadingLevel])

  // Only render if there are 3+ headings
  if (headings.length < 3) {
    return <></>
  }

  // Build nested structure: H2 → H3 → H4 → H5
  const nested = []
  let currentH2 = null
  let currentH3 = null
  let currentH4 = null

  for (const heading of headings) {
    if (heading.level === minHeadingLevel) {
      // H2 level
      currentH2 = { ...heading, children: [] }
      nested.push(currentH2)
      currentH3 = null
      currentH4 = null
    } else if (currentH2 && heading.level === minHeadingLevel + 1) {
      // H3 level
      currentH3 = { ...heading, children: [] }
      currentH2.children.push(currentH3)
      currentH4 = null
    } else if (currentH3 && heading.level === minHeadingLevel + 2) {
      // H4 level
      currentH4 = { ...heading, children: [] }
      currentH3.children.push(currentH4)
    } else if (currentH4 && heading.level === minHeadingLevel + 3) {
      // H5 level
      currentH4.children.push(heading)
    }
  }

  const visibleNested = isExpanded ? nested : nested.slice(0, initialVisibleItems)
  const hasMore = nested.length > initialVisibleItems

  const styles = {
    nav: {
      padding: '12px 16px',
      marginTop: '16px',
      marginBottom: '20px',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: '1px solid #e9ecef',
      maxWidth: '600px',
      fontSize: '14px'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '8px',
      cursor: hasMore ? 'pointer' : 'default'
    },
    title: {
      fontSize: '16px',
      fontWeight: '600',
      margin: 0,
      color: '#212529'
    },
    list: {
      listStyleType: 'decimal',
      listStylePosition: 'inside',
      margin: 0,
      padding: 0,
      lineHeight: '1.4'
    },
    listItem: {
      marginBottom: '4px'
    },
    link: {
      color: '#0066cc',
      textDecoration: 'none',
      transition: 'color 0.2s',
      fontSize: '14px'
    },
    nestedList: {
      listStyleType: 'lower-alpha',
      listStylePosition: 'inside',
      marginLeft: '16px',
      marginTop: '2px'
    },
    nestedListItem: {
      marginBottom: '2px'
    },
    nestedNestedList: {
      listStyleType: 'lower-roman',
      listStylePosition: 'inside',
      marginLeft: '16px',
      marginTop: '2px'
    },
    nestedNestedListItem: {
      marginBottom: '2px'
    },
    nestedNestedNestedList: {
      listStyleType: 'disc',
      listStylePosition: 'inside',
      marginLeft: '16px',
      marginTop: '2px'
    },
    nestedNestedNestedListItem: {
      marginBottom: '2px'
    },
    arrow: {
      fontSize: '12px',
      color: '#6c757d',
      transition: 'transform 0.2s',
      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
      userSelect: 'none'
    },
    toggleButton: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: '8px',
      padding: '4px 0',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#0066cc',
      fontWeight: '500',
      userSelect: 'none'
    }
  }

  return (
    <nav
      aria-label='Table of Contents'
      className={`article-tableOfContents ${className}`}
      style={styles.nav}
    >
      <div>
        <div
          style={styles.titleContainer}
          onClick={hasMore ? handleToggleExpand : undefined}
        >
          <h2 style={styles.title}>{titleText}</h2>
          <span style={styles.arrow}>▼</span>
        </div>
      </div>
      <ol style={styles.list}>
        {visibleNested.map((h2) => {
          return (
            <li key={h2.slug} style={styles.listItem}>
              <a
                href={`#${h2.slug}`}
                style={styles.link}
                onClick={(e) => handleClickLink(e, h2.slug)}
                onMouseEnter={handleLinkMouseEnter}
                onMouseLeave={handleLinkMouseLeave}
              >
                {h2.text}
              </a>
              {h2.children.length > 0 && (
                <ol style={styles.nestedList}>
                  {(isExpanded ? h2.children : h2.children.slice(0, initialVisibleNestedItems)).map((h3) => {
                    return (
                      <li key={h3.slug} style={styles.nestedListItem}>
                        <a
                          href={`#${h3.slug}`}
                          style={styles.link}
                          onClick={(e) => handleClickLink(e, h3.slug)}
                          onMouseEnter={handleLinkMouseEnter}
                          onMouseLeave={handleLinkMouseLeave}
                        >
                          {h3.text}
                        </a>
                        {h3.children && h3.children.length > 0 && (
                          <ol style={styles.nestedNestedList}>
                            {(isExpanded ? h3.children : h3.children.slice(0, initialVisibleNestedItems)).map((h4) => {
                              return (
                                <li key={h4.slug} style={styles.nestedNestedListItem}>
                                  <a
                                    href={`#${h4.slug}`}
                                    style={styles.link}
                                    onClick={(e) => handleClickLink(e, h4.slug)}
                                    onMouseEnter={handleLinkMouseEnter}
                                    onMouseLeave={handleLinkMouseLeave}
                                  >
                                    {h4.text}
                                  </a>
                                  {h4.children && h4.children.length > 0 && (
                                    <ol style={styles.nestedNestedNestedList}>
                                      {(isExpanded ? h4.children : h4.children.slice(0, initialVisibleNestedItems)).map((h5) => {
                                        return (
                                          <li key={h5.slug} style={styles.nestedNestedNestedListItem}>
                                            <a
                                              href={`#${h5.slug}`}
                                              style={styles.link}
                                              onClick={(e) => handleClickLink(e, h5.slug)}
                                              onMouseEnter={handleLinkMouseEnter}
                                              onMouseLeave={handleLinkMouseLeave}
                                            >
                                              {h5.text}
                                            </a>
                                          </li>
                                        )
                                      })}
                                    </ol>
                                  )}
                                </li>
                              )
                            })}
                          </ol>
                        )}
                      </li>
                    )
                  })}
                </ol>
              )}
            </li>
          )
        })}
      </ol>
      {hasMore && (
        <div style={styles.toggleButton} onClick={handleToggleExpand}>
          {isExpanded ? 'Read less' : 'Read more'}
        </div>
      )}
    </nav>
  )
}
